// src/components/Layout/Layout.js
import React, { useState, useEffect, useCallback, memo, useMemo } from "react";
import { Layout, Spin } from "antd";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import TopNav from "../TopNav/TopNav";
import SideMenu from "../SideMenu/SideMenu";
import menuLabelMap from "../menuLabelMap";
import BlankOutletWrapper from "./BlankOutletWrapper";
import ErrorBoundary from "../ErrorBoundary";
import useDynamicModules from "../../hooks/useDynamicModules";
import "./Layout2.css";

import PromotionNudgeModal, { usePromotionNudge } from "../Promotions/PromotionNudgeModal";
import { useAuth } from "../../context/AuthContext";

import "antd/dist/reset.css";
import "./global.css";
import "../ui/hms-ui.css";

const { Sider, Header, Content } = Layout;

const normalize = (v) => String(v ?? "").toLowerCase().replace(/\s+/g, "");
const normalizePath = (p) => {
  const s = String(p ?? "");
  if (!s) return "";
  return s.startsWith("/") ? s.toLowerCase() : `/${s}`.toLowerCase();
};

const AppLayout = () => {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem("hms_sidebar_collapsed") === "true";
    } catch {
      return false;
    }
  });
  const [isDarkMode] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const { authData } = useAuth();

  //  useDynamicModules returns a react-query result (data/isLoading)
  const { data: userMenus = [], isLoading: menusLoading, error: menusError } = useDynamicModules();

  // Marks <body> as the signed-in app so portals (dialogs, dropdowns, drawers) get the same font and background.
  useEffect(() => {
    document.body.classList.add("hms-app");
    return () => document.body.classList.remove("hms-app");
  }, []);

  const toggleSidebar = useCallback(
    () =>
      setCollapsed((prev) => {
        const next = !prev;
        try {
          localStorage.setItem("hms_sidebar_collapsed", String(next));
        } catch {
          /* storage unavailable */
        }
        return next;
      }),
    []
  );

  //  only show welcome on dashboard landing routes (avoid affecting other pages)
  const isDashboardRoute = useMemo(
    () => /\/(admin|hotels)\/dashboard(\/|$)/i.test(location.pathname),
    [location.pathname]
  );

  //  Determine if user has a Dashboard route assigned (so /hotels/dashboard won’t be blank)
  const hasDashboardModule = useMemo(() => {
    return (userMenus || []).some((m) => {
      const component = normalize(m?.component);
      const path = normalizePath(m?.path);

      // adapt to your payload: component OR path may represent dashboard
      if (component === "dashboard" || component === "admindashboard") return true;
      if (path === "/hotels/dashboard" || path === "/admin/dashboard") return true;
      return false;
    });
  }, [userMenus]);

  //  show welcome when user hits dashboard but has no dashboard module yet
  const shouldShowWelcome = useMemo(() => {
    if (menusLoading) return false;
    if (!isDashboardRoute) return false;

    // If menu API returned nothing or dashboard not assigned, show onboarding.
    // (This matches your “new user has only ModuleAssignment” scenario.)
    return !hasDashboardModule;
  }, [menusLoading, isDashboardRoute, hasDashboardModule]);

  // Page title for the top bar: label of the menu module that matches the current route.
  const pageTitle = useMemo(() => {
    const path = normalizePath(location.pathname).replace(/\/+$/, "");
    let best = null;
    let bestLen = -1;
    (userMenus || []).forEach((m) => {
      const p = normalizePath(m?.path).replace(/\/+$/, "");
      if (p && (path === p || path.startsWith(p + "/")) && p.length > bestLen) {
        best = m;
        bestLen = p.length;
      }
    });
    if (best) return menuLabelMap[best.component] ?? best.friendlyName ?? best.component ?? "";
    const seg = path.split("/").filter(Boolean).pop() || "";
    return seg.replace(/[-_]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  }, [location.pathname, userMenus]);

  //  Promotion nudge rules
  const hasHotelContext = !!authData?.hotelId;
  const isOnPromotionsPage = location.pathname.toLowerCase().includes("promotionspage");

  const nudge = usePromotionNudge({
    enabled: !menusLoading && hasHotelContext && !isOnPromotionsPage,
    showChance: 0.6,
    oncePerSession: true,
    weekendOnly: true,
  });

  //console.log("[Layout] Menu count:", userMenus?.length);
  //console.log("[Layout] hasDashboardModule:", hasDashboardModule);
  //console.log("[Layout] Show welcome:", shouldShowWelcome);
  //console.log("[Layout] Current path:", location.pathname);

  if (menusLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <Spin size="large" tip="Loading..." />
      </div>
    );
  }

  // Optional: if menu load failed, still render layout, but no welcome logic
  if (menusError) {
    console.warn("[Layout] Menu load error:", menusError);
  }

  return (
    <div className={`layout-root ${isDarkMode ? "dark-mode" : ""}`}>
      {/* Weekend/Friday Promotion Nudge */}
      <PromotionNudgeModal
        open={nudge.open}
        onClose={nudge.close}
        displayName={authData?.fullName || authData?.name || "Lesego"}
        onRunPromotion={() => {
          nudge.close();
          navigate("/hotels/promotionspage?create=1&source=weekend-nudge");
        }}
      />

      <Layout style={{ minHeight: "100vh" }}>
        {/* LEFT: vertical navigation */}
        <Sider
          width={230}
          collapsedWidth={60}
          collapsible
          trigger={null}
          collapsed={collapsed}
          theme="light"
          className="items-sider hms-sider"
        >
          <SideMenu isOpen={!collapsed} />
        </Sider>

        {/* RIGHT: header + page content */}
        <Layout>
          <Header className="hms-header">
            <TopNav
              toggleSidebar={toggleSidebar}
              isSidebarOpen={!collapsed}
              title={pageTitle}
            />
          </Header>

          <Content className="app-content">
            <div className="content-wrap">
              <div className="container">
                <div className="content-card">
                  {shouldShowWelcome ? (
                    <BlankOutletWrapper showWelcome />
                  ) : (
                    <ErrorBoundary resetKey={location.pathname}>
                      <Outlet />
                    </ErrorBoundary>
                  )}
                </div>
              </div>
            </div>
          </Content>
        </Layout>
      </Layout>
    </div>
  );
};

export default memo(AppLayout);
